import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, ArrowLeft, Loader2, Send, AlertTriangle, Lightbulb,
  Bug, FileText, Building2, CheckCircle, Clock, ChevronDown, ChevronUp
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const CATEGORIES = [
  { id: "insurer_issue", label: "Problème assureur", icon: Building2, color: "text-red-600" },
  { id: "bug", label: "Bug technique", icon: Bug, color: "text-amber-600" },
  { id: "suggestion", label: "Suggestion", icon: Lightbulb, color: "text-sky-600" },
  { id: "data_correction", label: "Correction de données", icon: FileText, color: "text-violet-600" },
  { id: "other", label: "Autre", icon: MessageSquare, color: "text-slate-600" },
];

const STATUS_CONFIG = {
  open: { label: "Ouvert", color: "bg-sky-100 text-sky-700" },
  in_progress: { label: "En cours", color: "bg-amber-100 text-amber-700" },
  resolved: { label: "Résolu", color: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Fermé", color: "bg-slate-100 text-slate-600" },
};

export default function FeedbackPage() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    category: "insurer_issue",
    subject: "",
    message: "",
    related_insurer: "",
    priority: "normal",
  });
  const navigate = useNavigate();

  useEffect(() => { loadFeedbacks(); }, []);

  const loadFeedbacks = async () => {
    try {
      const res = await axios.get(`${API}/feedback`, { headers: getHeaders() });
      setFeedbacks(res.data.feedbacks || []);
    } catch {}
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!form.subject || !form.message) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, form, { headers: getHeaders() });
      toast.success("Signalement envoyé avec succès !");
      setShowForm(false);
      setForm({ category: "insurer_issue", subject: "", message: "", related_insurer: "", priority: "normal" });
      loadFeedbacks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-slate-600" data-testid="feedback-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Signalements & Suggestions
              </h1>
              <p className="text-sm text-slate-500">Remontez vos informations et suggestions</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full" data-testid="new-feedback-btn">
            <Send className="w-4 h-4 mr-2" /> Nouveau signalement
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <Card className="bg-white border-sky-200 mb-6" data-testid="feedback-form">
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-2">Catégorie</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setForm({ ...form, category: cat.id })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        form.category === cat.id ? "bg-sky-50 border-sky-300 text-sky-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <cat.icon className={`w-3.5 h-3.5 ${form.category === cat.id ? "text-sky-600" : cat.color}`} />
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Sujet</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Ex: Retard de paiement NSIA..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50 focus:ring-2 focus:ring-sky-500/20"
                  data-testid="feedback-subject"
                />
              </div>
              {form.category === "insurer_issue" && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Assureur concerné</label>
                  <input
                    type="text"
                    value={form.related_insurer}
                    onChange={(e) => setForm({ ...form, related_insurer: e.target.value })}
                    placeholder="Nom de l'assureur"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50"
                    data-testid="feedback-insurer"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  placeholder="Décrivez le problème ou votre suggestion en détail..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50 resize-none focus:ring-2 focus:ring-sky-500/20"
                  data-testid="feedback-message"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Priorité</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50"
                  data-testid="feedback-priority"
                >
                  <option value="low">Basse</option>
                  <option value="normal">Normale</option>
                  <option value="high">Haute</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={submit} disabled={sending} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="feedback-submit">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <div className="space-y-3" data-testid="feedback-list">
          {feedbacks.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucun signalement pour le moment</p>
                <p className="text-xs text-slate-400 mt-1">Cliquez sur "Nouveau signalement" pour en créer un</p>
              </CardContent>
            </Card>
          ) : (
            feedbacks.map((fb, i) => {
              const statusCfg = STATUS_CONFIG[fb.status] || STATUS_CONFIG.open;
              const cat = CATEGORIES.find((c) => c.id === fb.category) || CATEGORIES[4];
              const isOpen = expanded === i;
              return (
                <Card key={fb.id} className="bg-white border-slate-200" data-testid={`feedback-${fb.id}`}>
                  <button onClick={() => setExpanded(isOpen ? null : i)} className="w-full text-left">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <cat.icon className={`w-5 h-5 ${cat.color}`} />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{fb.subject}</p>
                            <p className="text-xs text-slate-500">
                              {cat.label} | {new Date(fb.created_at).toLocaleDateString("fr-FR")}
                              {fb.related_insurer && ` | ${fb.related_insurer}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                    </CardContent>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                      <p className="text-sm text-slate-700 leading-relaxed mt-3">{fb.message}</p>
                      {fb.admin_response && (
                        <div className="mt-3 bg-sky-50 border border-sky-100 rounded-lg p-3">
                          <p className="text-xs font-semibold text-sky-700 mb-1">Réponse de l'administration</p>
                          <p className="text-sm text-sky-900">{fb.admin_response}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
